/**
 * Admin Authentication Utility
 *
 * Admin sign-in (web2): the signed-in Supabase account → POST /admin/session/supabase → 24h
 * session token (createAdminSessionFromSupabase). The wallet challenge below is PARKED — kept
 * working server-side for possible reuse, no longer called by the Admin page:
 *  1. GET /admin/challenge  → nonce
 *  2. Sign nonce with wallet (caller's responsibility)
 *  3. POST /admin/session   → 24h session token
 *  4. All requests use Authorization: Bearer <token>
 */

import { API_BASE_URL } from '../config/apiConfig';
import type { BondInsightsResponse } from '@shared/api/contracts';

const SESSION_TOKEN_KEY = 'admin_session_token';

// ── Token storage ────────────────────────────────────────────────────────────

export function saveAdminToken(token: string): void {
  localStorage.setItem(SESSION_TOKEN_KEY, token);
}

export function loadAdminToken(): string | null {
  return localStorage.getItem(SESSION_TOKEN_KEY);
}

export function clearAdminToken(): void {
  localStorage.removeItem(SESSION_TOKEN_KEY);
}

/** Build Authorization header object for admin requests. */
function adminHeaders(token: string): Record<string, string> {
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
  };
}

// ── Challenge / session ───────────────────────────────────────────────────────

/** Fetch a one-time challenge nonce from the server. */
export async function fetchAdminChallenge(): Promise<string> {
  const response = await fetch(`${API_BASE_URL}/api/v1/admin/challenge`);
  if (!response.ok) throw new Error('Failed to fetch challenge');
  const result = await response.json();
  return result.nonce as string;
}

export interface CreateSessionResponse {
  success: boolean;
  token?: string;
  error?: string;
}

/**
 * Exchange a signed nonce for a 24h session token.
 * `signature` must be the base64-encoded Sui personal message signature.
 */
export async function createAdminSession(
  wallet: string,
  nonce: string,
  signature: string
): Promise<CreateSessionResponse> {
  const response = await fetch(`${API_BASE_URL}/api/v1/admin/session`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ wallet, nonce, signature }),
  });
  return response.json();
}

/**
 * Web2 admin sign-in: exchange the signed-in Supabase account's access token for a 24h admin
 * session. The backend grants it only to a confirmed email listed in ADMIN_EMAILS.
 */
export async function createAdminSessionFromSupabase(accessToken: string): Promise<CreateSessionResponse> {
  const response = await fetch(`${API_BASE_URL}/api/v1/admin/session/supabase`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ accessToken }),
  });
  return response.json();
}

export interface AdminVerifyResponse {
  success: boolean;
  message?: string;
  error?: string;
  wallet?: string;
}

/**
 * Verify that the stored session token is still valid.
 * Returns the wallet address on success, null otherwise.
 */
export async function verifyAdminToken(token: string): Promise<string | null> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/v1/admin/verify`, {
      method: 'POST',
      headers: adminHeaders(token),
    });
    if (!response.ok) return null;
    const result: AdminVerifyResponse = await response.json();
    return result.success ? (result.wallet ?? null) : null;
  } catch {
    return null;
  }
}

/**
 * Legacy helper — kept for callers that only need a boolean.
 * Checks the stored token; does NOT trigger a new sign flow.
 */
export async function verifyAdminAccess(_walletAddress: string | null): Promise<boolean> {
  const token = loadAdminToken();
  if (!token) return false;
  const wallet = await verifyAdminToken(token);
  return wallet !== null;
}

/**
 * Fetch wallet 360 analysis with admin authentication
 */
export async function fetchWallet360Analysis(walletAddress: string, _adminWallet: string): Promise<unknown> {
  const token = loadAdminToken() ?? '';
  const response = await fetch(
    `${API_BASE_URL}/api/v1/admin/wallet/${encodeURIComponent(walletAddress)}`,
    { headers: { 'Authorization': `Bearer ${token}` } }
  );

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to load wallet data');
  }

  const result = await response.json();
  // API wraps data in a 'data' property
  return result.data || result;
}

export interface WalletsListResponse {
  data: unknown[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
}

/**
 * Fetch summary of all wallets with admin authentication (paginated)
 */
export async function fetchAllWalletsSummary(
  _adminWallet: string,
  page: number = 1,
  pageSize: number = 20,
  search?: string
): Promise<WalletsListResponse> {
  const token = loadAdminToken() ?? '';
  const params = new URLSearchParams();
  params.append('page', page.toString());
  params.append('pageSize', pageSize.toString());
  if (search) {
    params.append('search', search);
  }

  const response = await fetch(
    `${API_BASE_URL}/api/v1/admin/wallets?${params.toString()}`,
    { headers: { 'Authorization': `Bearer ${token}` } }
  );

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to load wallets summary');
  }

  return response.json();
}

export interface PetSearchResult {
  pet_id: string;
  name: string;
  species_id: number;
  suika_balance: number;
}

/**
 * Search pets by name (case-insensitive partial match)
 */
export async function searchPetsByName(
  _adminWallet: string,
  query: string
): Promise<PetSearchResult[]> {
  const token = loadAdminToken() ?? '';
  const response = await fetch(
    `${API_BASE_URL}/api/v1/admin/search-pets?q=${encodeURIComponent(query)}`,
    { headers: { 'Authorization': `Bearer ${token}` } }
  );

  if (!response.ok) return [];

  const result = await response.json();
  return result.data || [];
}

export interface GrantSuikaResponse {
  success: boolean;
  message?: string;
  data?: {
    petId: string;
    petName: string;
    previousBalance: number;
    newBalance: number;
    granted: number;
  };
}

/**
 * Grant 1000 Suika to a pet by ID
 */
export async function grantSuikaByPetId(
  _adminWallet: string,
  petId: string
): Promise<GrantSuikaResponse> {
  const token = loadAdminToken() ?? '';
  const response = await fetch(`${API_BASE_URL}/api/v1/admin/grant-suika`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
    body: JSON.stringify({ petId }),
  });

  const result = await response.json();

  if (!response.ok) {
    return { success: false, message: result.message || 'Failed to grant Suika' };
  }

  return result;
}

export interface BackendPet {
  petId: string;
  name: string;
  species: number;
  stage: number;
  level: number;
  imageUrl: string;
  /** 'in_custody' = linked to a player; 'reserve' = game-owned (sold/donated back). */
  custodyStatus?: 'in_custody' | 'reserve';
  /** The linked player's address (null for reserve pets). */
  ownerAddress?: string | null;
}

/**
 * Fetch all Suikamon NFTs owned by the backend wallet
 */
export async function fetchBackendPets(_adminWallet: string): Promise<BackendPet[]> {
  const token = loadAdminToken() ?? '';
  const response = await fetch(`${API_BASE_URL}/api/v1/admin/backend-pets`, {
    headers: { 'Authorization': `Bearer ${token}` },
  });

  if (!response.ok) return [];

  const result = await response.json();
  return result.data || [];
}

/**
 * A Suikamon row for the admin table — sourced from the DB, so it includes EVERY
 * custody status (including player-held pets withdrawn to their own wallet), not just
 * backend/sponsor-owned ones.
 */
export interface AdminPet {
  petId: string;
  name: string;
  species: number;
  stage: number;
  level: number;
  custodyStatus: CustodyStatusName;
  ownerAddress: string | null;
}

/** List every Suikamon in the DB (all custody statuses), paginated past the row cap server-side. */
export async function fetchAllPets(_adminWallet: string): Promise<AdminPet[]> {
  const token = loadAdminToken() ?? '';
  const response = await fetch(`${API_BASE_URL}/api/v1/admin/all-pets`, {
    headers: { 'Authorization': `Bearer ${token}` },
  });

  if (!response.ok) return [];

  const result = await response.json();
  return result.data || [];
}

/**
 * A guest (free-tier, unauthenticated) pet row for the admin ledger — name, species,
 * current level, and when it first hit the free-play level cap (free play stopped,
 * conversion required). `freeCapReachedAt` is null until the pet reaches the cap.
 */
export interface GuestPetLedgerItem {
  petId: string;
  name: string;
  species: number;
  level: number;
  freeCapReachedAt: string | null;
}

/** List every guest (free-tier) Suikamon with its level-cap status, for the admin Guests panel. */
export async function fetchGuestLedger(_adminWallet: string): Promise<GuestPetLedgerItem[]> {
  const token = loadAdminToken() ?? '';
  const response = await fetch(`${API_BASE_URL}/api/v1/admin/guest-ledger`, {
    headers: { 'Authorization': `Bearer ${token}` },
  });

  if (!response.ok) return [];

  const result = await response.json();
  return result.data || [];
}

export interface RenamePetResponse {
  success: boolean;
  message?: string;
  data?: {
    petId: string;
    newName: string;
    digest: string;
  };
}

/**
 * Rename a backend-owned Suikamon NFT on-chain
 */
export async function renamePet(
  _adminWallet: string,
  petId: string,
  newName: string
): Promise<RenamePetResponse> {
  const token = loadAdminToken() ?? '';
  const response = await fetch(`${API_BASE_URL}/api/v1/admin/rename-pet`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
    body: JSON.stringify({ petId, newName }),
  });

  const result = await response.json();

  if (!response.ok) {
    return { success: false, message: result.message || 'Failed to rename pet' };
  }

  return result;
}

export interface AssignReservePetResponse {
  success: boolean;
  message?: string;
  data?: {
    petId: string;
    newOwner: string;
  };
}

/**
 * Admin: assign a RESERVE pet to a wallet. DB-only reassign (the NFT stays in custody and the
 * pet becomes a normal in_custody pet owned by the target address).
 */
export async function assignReservePet(
  petId: string,
  toAddress: string,
): Promise<AssignReservePetResponse> {
  const token = loadAdminToken() ?? '';
  const response = await fetch(`${API_BASE_URL}/api/v1/admin/assign-reserve-pet`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
    body: JSON.stringify({ petId, toAddress }),
  });

  const result = await response.json();

  if (!response.ok) {
    return { success: false, message: result.message || 'Failed to assign reserve pet' };
  }

  return result;
}

export type CustodyStatusName = 'in_custody' | 'reserve' | 'pending_deposit' | 'withdrawn';

// ── Pet Detail (Suikamon Dashboard) ──

export interface PetDetailAction {
  id: string;
  pet_id: string;
  action_type: string;
  xp_gained: number;
  created_at: string;
}

export interface DailyActionPoint {
  date: string;
  count: number;
}

export interface PetDetailResponse {
  success: boolean;
  data?: {
    pet: Record<string, unknown>;
    actions: PetDetailAction[];
    dailyActions: DailyActionPoint[];
  };
  message?: string;
}

/**
 * Fetch detailed pet info + action history with optional time filters
 */
export async function fetchPetDetail(
  _adminWallet: string,
  petId: string,
  from?: string,
  to?: string
): Promise<PetDetailResponse> {
  const token = loadAdminToken() ?? '';
  const params = new URLSearchParams();
  if (from) params.append('from', from);
  if (to) params.append('to', to);

  const qs = params.toString() ? `?${params.toString()}` : '';
  const response = await fetch(
    `${API_BASE_URL}/api/v1/admin/pet-detail/${encodeURIComponent(petId)}${qs}`,
    { headers: { 'Authorization': `Bearer ${token}` } }
  );

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    return { success: false, message: err.message || 'Failed to fetch pet detail' };
  }

  return response.json();
}

export interface ResetSeasonEloResponse {
  success: boolean;
  message?: string;
  petsUpdated?: number | null;
  resetTo?: number;
  closedSeason?: string;
  newSeason?: string;
  snapshotted?: number;
  petsReset?: number;
}

/**
 * Reset all pets' ELO ratings to the default starting value (1200)
 */
export async function resetSeasonElo(_adminWallet: string): Promise<ResetSeasonEloResponse> {
  const token = loadAdminToken() ?? '';
  const response = await fetch(`${API_BASE_URL}/api/v1/admin/reset-season-elo`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
  });

  const result = await response.json();

  if (!response.ok) {
    return { success: false, message: result.message || 'Failed to reset season ELO' };
  }

  return result;
}

// ── Species supply reconciliation audit + resync ──

export interface SpeciesSupplyAuditRow {
  speciesId: number;
  rarity: string;
  /** Authoritative on-chain registry count (null if the read failed). */
  registryCount: number | null;
  /** pet_state rows we actually have (playable game state). */
  dbCount: number;
  /** Pets the public RPC's queryEvents history still returns (null if read failed). */
  eventCount: number | null;
  /** Custody-held NFTs enumerable on-chain right now (null if read failed). */
  sponsorOwnedCount: number | null;
}

export async function fetchSpeciesSupplyAudit(_adminWallet: string): Promise<SpeciesSupplyAuditRow[]> {
  const token = loadAdminToken() ?? '';
  const response = await fetch(`${API_BASE_URL}/api/v1/admin/species-supply-audit`, {
    headers: { 'Authorization': `Bearer ${token}` },
  });

  if (!response.ok) return [];

  const result = await response.json();
  return result.data || [];
}

export interface ResyncSupplyResponse {
  success: boolean;
  message?: string;
  data?: {
    total: number;
    synced: number;
    updated: number;
    failed: number;
    duration: number;
  };
}

/**
 * THE FIX: reconcile the DB against the chain — creates pet_state rows for any minted
 * Suikamon missing from the DB (event-discovered ∪ sponsor-owned).
 */
export async function resyncSupply(_adminWallet: string): Promise<ResyncSupplyResponse> {
  const token = loadAdminToken() ?? '';
  const response = await fetch(`${API_BASE_URL}/api/v1/admin/resync-supply`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
  });

  const result = await response.json();

  if (!response.ok) {
    return { success: false, message: result.message || 'Failed to resync supply' };
  }

  return result;
}

export interface SpeciesWinRate {
  speciesId: number;
  totalBattles: number;
  wins: number;
  losses: number;
  draws: number;
  winRate: number;
  lossRate: number;
  drawRate: number;
}

export interface SpeciesWinRateResponse {
  success: boolean;
  data: SpeciesWinRate[];
  mode: string;
  message?: string;
  error?: string;
}

/**
 * Fetch species win rate statistics with admin authentication
 */
export async function fetchSpeciesWinRates(
  _adminWallet: string,
  mode?: string,
  version?: string
): Promise<SpeciesWinRateResponse> {
  const token = loadAdminToken() ?? '';
  const params = new URLSearchParams();
  if (mode && mode !== 'all') {
    params.append('mode', mode);
  }
  if (version && version !== 'all') {
    params.append('version', version);
  }

  const url = `${API_BASE_URL}/api/v1/admin/species-win-rates${
    params.toString() ? `?${params.toString()}` : ''
  }`;

  const response = await fetch(url, {
    headers: { 'Authorization': `Bearer ${token}` },
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to load species win rates');
  }

  return response.json();
}

// ── Companion Bonding insights (feedback / community / personalization) ──

/**
 * Fetch aggregated companion-bonding insights: feedback sentiment per prompt,
 * community CTA yes/not-yet conversion, and personalization fact distributions.
 * `since` (ISO date string) optionally scopes the aggregation window.
 */
export async function fetchBondInsights(
  _adminWallet: string,
  since?: string
): Promise<BondInsightsResponse> {
  const token = loadAdminToken() ?? '';
  const params = new URLSearchParams();
  if (since) params.append('since', since);

  const qs = params.toString() ? `?${params.toString()}` : '';
  const response = await fetch(`${API_BASE_URL}/api/v1/admin/bond-insights${qs}`, {
    headers: { 'Authorization': `Bearer ${token}` },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to load bond insights');
  }

  const result = await response.json();
  return result.data ?? result;
}
